import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, Loader2 } from "lucide-react";

const TIME_SLOTS = ["09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM", "06:00 PM"];

export default function ScheduleVisitDialog({ hostel, open, onClose, user }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("11:00 AM");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setPhone(user?.phone || "");
      setDone(false);
    }
  }, [open, user]);

  const minDate = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (name.trim().length < 2) { toast.error("Please enter your name"); return; }
    if (digits.length !== 10) { toast.error("Please enter a valid 10-digit phone number"); return; }
    if (!visitDate) { toast.error("Please pick a visit date"); return; }
    setLoading(true);
    try {
      await api.post("/visits", {
        hostel_id: hostel.id,
        name: name.trim(),
        phone: digits,
        visit_date: visitDate,
        visit_time: visitTime,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule visit");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="schedule-visit-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="visit-confirmed-panel">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <h3 className="font-display font-black text-2xl mt-3">Visit scheduled!</h3>
            <p className="text-sm text-slate-600 mt-2">
              Your visit to <b>{hostel.name}</b> on <b>{visitDate}</b> at <b>{visitTime}</b> is confirmed.
              The owner has been notified and you'll receive a confirmation on WhatsApp{user?.email ? " and email" : ""}.
            </p>
            <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mt-4">
              Visits are always free — never pay anything before booking through TheHostelNow.com.
            </p>
            <Button data-testid="visit-done-btn" onClick={onClose} className="mt-5 rounded-full bg-orange-800 hover:bg-orange-900 text-white px-8">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <CalendarClock size={20} className="text-orange-800" /> Schedule a free visit
              </DialogTitle>
              <DialogDescription>
                See {hostel.name} in person before booking. Free of cost — the owner will be notified instantly.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid gap-3 mt-1">
              <div>
                <Label htmlFor="sv-name" className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Your name</Label>
                <Input id="sv-name" data-testid="visit-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="sv-phone" className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Phone (WhatsApp)</Label>
                <Input id="sv-phone" data-testid="visit-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" inputMode="numeric" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="sv-date" className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Preferred date</Label>
                  <Input id="sv-date" data-testid="visit-date-input" type="date" min={minDate} value={visitDate} onChange={(e) => setVisitDate(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Preferred time</Label>
                  <Select value={visitTime} onValueChange={setVisitTime}>
                    <SelectTrigger data-testid="visit-time-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="visit-submit-btn" type="submit" disabled={loading} className="mt-2 w-full rounded-full bg-orange-800 hover:bg-orange-900 text-white h-11 font-semibold gap-2">
                {loading ? <><Loader2 size={15} className="animate-spin" /> Scheduling…</> : <><CalendarClock size={15} /> Schedule visit — it's free</>}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">No payment needed. You'll get a WhatsApp confirmation instantly.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
